import { useState, useEffect } from "react";
import axios from "axios";
import { BUSINESS, SERVICE_OPTIONS } from "@/data";
import * as Icons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  device_type: "iPhone",
  device_model: "",
  issue: "",
  service_needed: "screen_repair",
  contact_pref: "call",
};

export default function QuoteDialog({ open, onClose, prefill }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Apply prefill values whenever the dialog is opened with a prefill payload
  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({ ...f, ...prefill }));
      setDone(false);
      setError("");
    }
  }, [open, prefill]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
          : "Could not submit. Please call us directly.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setDone(false);
    setError("");
    onClose();
  };

  return (
    <div
      data-testid="quote-dialog"
      className="fixed inset-0 z-[100] grid place-items-center p-4 md:p-6 animate-fade-up"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-title"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={reset}
        data-testid="quote-overlay"
        className="absolute inset-0 bg-brand-navy/60 backdrop-blur-md"
      />

      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl border border-slate-200 shadow-2xl">
        <button
          type="button"
          onClick={reset}
          data-testid="quote-close-btn"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center transition-all z-10"
          aria-label="Close quote form"
        >
          <Icons.X className="w-4 h-4 text-brand-navy" />
        </button>

        {done ? (
          <div className="p-8 md:p-12 text-center" data-testid="quote-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-brand-red grid place-items-center glow-red animate-pulse-red">
              <Icons.Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-black text-brand-navy mt-6 tracking-tight">Thanks! We got it.</h3>
            <p className="text-slate-600 mt-3 max-w-md mx-auto">
              Your Phone Plug will contact you shortly. For faster service, call us right now.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-2.5 justify-center">
              <a
                href={`tel:${BUSINESS.phoneTel}`}
                data-testid="quote-success-call"
                className="inline-flex items-center justify-center gap-2 font-bold rounded-full py-3.5 px-6 bg-brand-red hover:brightness-110 text-white animate-pulse-red transition-all active:scale-95"
              >
                <Icons.Phone className="w-4 h-4" />
                Call {BUSINESS.phone}
              </a>
              <button
                onClick={reset}
                data-testid="quote-success-close"
                className="inline-flex items-center justify-center gap-2 font-bold rounded-full py-3.5 px-6 bg-slate-100 hover:bg-slate-200 text-brand-navy transition-all"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-10" data-testid="quote-form">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-100 mb-4">
              <Icons.MessageSquareQuote className="w-3.5 h-3.5 text-brand-red" />
              <span className="text-xs font-bold uppercase tracking-widest text-brand-red">Free Quote</span>
            </div>
            <h3 id="quote-title" className="font-display text-3xl md:text-4xl font-black text-brand-navy tracking-tight">Get a Free Quote</h3>
            <p className="text-sm text-slate-600 mt-2">Fill out a quick form. We'll get back to you fast.</p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Your Name" required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="quote-name"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all"
                  placeholder="Marcus T."
                />
              </Field>

              <Field label="Phone Number" required>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="quote-phone"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all"
                  placeholder="(786) 555-0123"
                />
              </Field>

              <Field label="Device Type" required>
                <select
                  value={form.device_type}
                  onChange={update("device_type")}
                  data-testid="quote-device-type"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy focus:outline-none focus:border-brand-red focus:bg-white transition-all appearance-none"
                >
                  {["iPhone", "Samsung", "Android (other)", "iPad", "Tablet", "Apple Watch", "Laptop", "Other"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>

              <Field label="Device Model" required>
                <input
                  required
                  value={form.device_model}
                  onChange={update("device_model")}
                  data-testid="quote-device-model"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all"
                  placeholder="iPhone 14 Pro"
                />
              </Field>

              <Field label="Service Needed" required>
                <select
                  value={form.service_needed}
                  onChange={update("service_needed")}
                  data-testid="quote-service"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy focus:outline-none focus:border-brand-red focus:bg-white transition-all appearance-none"
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Preferred Contact" required>
                <div className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200" role="radiogroup">
                  {[
                    { value: "call", label: "Call", icon: Icons.Phone },
                    { value: "text", label: "Text", icon: Icons.MessageSquare },
                    { value: "instagram", label: "DM", icon: Icons.Instagram },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = form.contact_pref === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm((f) => ({ ...f, contact_pref: opt.value }))}
                        data-testid={`quote-contact-${opt.value}`}
                        className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
                          active ? "bg-brand-red text-white shadow-sm" : "text-slate-600 hover:bg-white"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="md:col-span-2">
                <Field label="What happened?" required>
                  <textarea
                    required
                    rows={4}
                    value={form.issue}
                    onChange={update("issue")}
                    data-testid="quote-issue"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-red focus:bg-white transition-all resize-none"
                    placeholder="Cracked the screen yesterday, touch still works but display has dark spots..."
                  />
                </Field>
              </div>
            </div>

            {error && (
              <div className="mt-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="quote-error">
                {error}
              </div>
            )}

            <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
              <a
                href={`tel:${BUSINESS.phoneTel}`}
                data-testid="quote-call-instead"
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-slate-600 hover:text-brand-red transition-colors"
              >
                <Icons.Phone className="w-4 h-4" />
                Or call {BUSINESS.phone}
              </a>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit"
                className="inline-flex items-center justify-center gap-2 font-bold rounded-full py-3.5 px-7 bg-brand-red hover:brightness-110 text-white animate-pulse-red transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Send className="w-4 h-4" />}
                {submitting ? "Sending..." : "Request Quote"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
        {label}
        {required && <span className="text-brand-red ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
